<template>
  <div class="tc-combobox" :class="{ 'is-open': open, 'is-disabled': disabled }" :data-testid="testid">
    <div
      ref="control"
      class="tc-combobox-control"
      role="combobox"
      :aria-expanded="open"
      aria-haspopup="listbox"
      :aria-controls="`${testid}-list`"
    >
      <!-- Idle value overlay: the selected code + name, styled. Sits over the
           input (pointer-events:none) so a click still lands on the input and
           focuses it; hidden while focused so typing reads as a search. -->
      <span
        v-if="modelValue && !focused"
        class="tc-combobox-value"
        :data-testid="`${testid}-value`"
      >
        <span v-if="modelValue.code" class="tc-combobox-value-code txt-mono">{{ modelValue.code }}</span>
        <span class="tc-combobox-value-name">{{ modelValue.name }}</span>
      </span>
      <input
        ref="input"
        type="text"
        class="tc-combobox-input"
        role="searchbox"
        aria-autocomplete="list"
        autocomplete="off"
        :placeholder="modelValue ? '' : placeholder"
        :disabled="disabled"
        :value="query"
        :aria-activedescendant="activeId"
        :data-testid="`${testid}-input`"
        @focus="onFocus"
        @input="onInput"
        @keydown="onKeydown"
      />
      <span class="tc-combobox-chevron" aria-hidden="true">▾</span>
    </div>

    <Teleport to="body">
      <div
        v-if="open"
        ref="floating"
        class="tc-combobox-panel"
        :class="{ 'is-searching': isSearching }"
        :style="floatingStyles"
        :data-testid="`${testid}-panel`"
        @mousedown.prevent
      >
        <div v-if="eyebrow" class="tc-combobox-eyebrow">{{ eyebrow }}</div>
        <ul
          :id="`${testid}-list`"
          ref="listEl"
          class="tc-combobox-list"
          role="listbox"
          @scroll="onScroll"
        >
          <li v-if="loading" class="tc-combobox-loading" :data-testid="`${testid}-loading`">
            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          </li>
          <li
            v-else-if="options.length === 0"
            class="tc-combobox-empty"
            :data-testid="`${testid}-empty`"
          >{{ emptyLabel }}</li>
          <li
            v-for="(opt, i) in options"
            v-else
            :id="`${testid}-opt-${i}`"
            :key="opt.id"
            class="tc-combobox-option"
            :class="{ 'is-active': i === activeIndex, 'is-selected': isSelected(opt) }"
            role="option"
            :aria-selected="isSelected(opt)"
            :data-testid="`${testid}-option`"
            @mousemove="activeIndex = i"
            @click="pick(opt)"
          >
            <span class="tc-combobox-check" aria-hidden="true">{{ isSelected(opt) ? '✓' : '' }}</span>
            <span class="tc-combobox-option-main">{{ opt.name }}</span>
            <span v-if="opt.code" class="tc-combobox-option-code txt-mono">{{ opt.code }}</span>
          </li>
        </ul>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onUnmounted } from 'vue';
import { useFloating, offset, flip, shift, size, autoUpdate } from '@floating-ui/vue';

// One searchable single-select field, used everywhere a project / phase / task
// is picked (ADR-0042): the attribution wizard, the inline row editor, the
// cell-edit card. The field IS the input — clicking it opens a floating overlay
// with a slim recents list; typing filters/searches in place and the panel
// grows. No trigger→search-box mode-switch, and the teleported panel never
// resizes the host modal.
//
// Presentational only: the host owns the data. It hands in `options` for the
// current query (recents when the query is empty) plus `loading`; the field
// emits keystrokes via `update:query` (debounce upstream) and the chosen option
// via `select`.
export interface ComboOption {
  id: string;
  name: string;
  code?: string;
  // Idle grouping in the wizard's project pane (resolve mode) — the pane renders
  // a heading when this changes. SearchCombobox itself ignores it.
  group?: 'suggested' | 'recent';
}

const props = withDefaults(defineProps<{
  modelValue: ComboOption | null;
  options: ComboOption[];
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  emptyLabel?: string;
  // Eyebrow over the list while the query is empty (the recents heading).
  recentLabel?: string;
  testid: string;
}>(), {
  loading: false,
  disabled: false,
  placeholder: 'Search…',
  emptyLabel: 'No matches.',
  recentLabel: 'Recent',
});

const emit = defineEmits<{
  'update:query': [value: string];
  select: [option: ComboOption];
}>();

const control = ref<HTMLElement | null>(null);
const input = ref<HTMLInputElement | null>(null);
const floating = ref<HTMLElement | null>(null);
const listEl = ref<HTMLElement | null>(null);

const open = ref(false);
const focused = ref(false);
const query = ref('');
const activeIndex = ref(-1);

const isSearching = computed(() => query.value.trim().length > 0);
const activeId = computed(() => (activeIndex.value >= 0 ? `${props.testid}-opt-${activeIndex.value}` : undefined));
const eyebrow = computed(() => (!isSearching.value && props.options.length > 0 ? props.recentLabel : ''));

const reference = computed(() => control.value);
const { floatingStyles } = useFloating(reference, floating, {
  placement: 'bottom-start',
  middleware: [
    offset(4),
    flip(),
    shift({ padding: 8 }),
    size({ apply({ rects, elements }) { elements.floating.style.width = `${rects.reference.width}px`; } }),
  ],
  whileElementsMounted: autoUpdate,
});

function isSelected(opt: ComboOption): boolean {
  return !!props.modelValue && props.modelValue.id === opt.id;
}

function openPanel(): void {
  if (open.value || props.disabled) return;
  open.value = true;
  activeIndex.value = -1;
  document.addEventListener('mousedown', onOutside);
}

function closePanel(): void {
  if (!open.value) return;
  open.value = false;
  focused.value = false;
  query.value = '';
  emit('update:query', '');
  document.removeEventListener('mousedown', onOutside);
}

function onFocus(): void {
  focused.value = true;
  // Show the current value as selectable text so the first keystroke replaces it.
  void nextTick(() => input.value?.select());
  openPanel();
}

function onInput(e: Event): void {
  query.value = (e.target as HTMLInputElement).value;
  activeIndex.value = -1;
  if (!open.value) openPanel();
  emit('update:query', query.value);
}

function pick(opt: ComboOption): void {
  emit('select', opt);
  closePanel();
  input.value?.blur();
}

function move(delta: number): void {
  const n = props.options.length;
  if (n === 0) return;
  activeIndex.value = activeIndex.value < 0 && delta < 0
    ? n - 1
    : (activeIndex.value + delta + n) % n;
  void nextTick(() => {
    floating.value?.querySelector(`#${props.testid}-opt-${activeIndex.value}`)?.scrollIntoView({ block: 'nearest' });
  });
}

function onKeydown(e: KeyboardEvent): void {
  switch (e.key) {
    case 'ArrowDown': e.preventDefault(); if (!open.value) openPanel(); else move(1); break;
    case 'ArrowUp': e.preventDefault(); move(-1); break;
    case 'Enter':
      if (open.value && activeIndex.value >= 0 && props.options[activeIndex.value]) {
        e.preventDefault();
        pick(props.options[activeIndex.value]);
      }
      break;
    // Escape closes the dropdown first and is swallowed, so it doesn't also
    // close a host modal; a second Escape (panel shut) falls through to the host.
    case 'Escape': if (open.value) { e.preventDefault(); e.stopPropagation(); closePanel(); input.value?.blur(); } break;
    case 'Tab': closePanel(); break;
  }
}

// Endless-scroll hook left to the host if needed; today the panel just scrolls.
function onScroll(): void { /* no-op: results are bounded by the backend search */ }

function onOutside(e: MouseEvent): void {
  const t = e.target as Node;
  if (control.value?.contains(t) || floating.value?.contains(t)) return;
  closePanel();
}

// Resetting the query when options change is the host's job (it recomputes
// options off the emitted query). Keep the active row in range if the list shrank.
watch(() => props.options.length, (n) => { if (activeIndex.value >= n) activeIndex.value = -1; });

onUnmounted(() => document.removeEventListener('mousedown', onOutside));
</script>

<style scoped>
.tc-combobox { position: relative; }

.tc-combobox-control {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 34px;
  padding: 0 var(--s-8) 0 var(--s-10);
  background: var(--bg-surface);
  border: 1px solid var(--stroke-2);
  border-radius: var(--r-md);
  cursor: text;
}
.tc-combobox-control:hover { border-color: var(--stroke-strong); }
.tc-combobox.is-open .tc-combobox-control,
.tc-combobox-control:focus-within {
  border-color: var(--stroke-focus);
  box-shadow: 0 0 0 1px var(--stroke-focus);
}
.tc-combobox.is-disabled .tc-combobox-control { background: var(--bg-subtle); cursor: not-allowed; opacity: .7; }

/* Idle value overlay — sits over the input, click passes through to focus it. */
.tc-combobox-value {
  position: absolute;
  left: var(--s-10);
  right: 28px;
  display: flex;
  align-items: baseline;
  gap: var(--s-8);
  pointer-events: none;
  overflow: hidden;
  white-space: nowrap;
}
.tc-combobox-value-code { flex-shrink: 0; font-size: var(--t-12); color: var(--fg-tertiary); }
.tc-combobox-value-name { overflow: hidden; text-overflow: ellipsis; color: var(--fg-primary); font-size: var(--t-14); }

.tc-combobox-input {
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  outline: none;
  font: inherit;
  font-size: var(--t-14);
  color: var(--fg-primary);
  padding: var(--s-6) 0;
}
.tc-combobox-input::placeholder { color: var(--fg-disabled); }

.tc-combobox-chevron { flex-shrink: 0; margin-left: var(--s-6); color: var(--fg-tertiary); transition: transform .15s ease; }
.tc-combobox.is-open .tc-combobox-chevron { transform: rotate(180deg); }
@media (prefers-reduced-motion: reduce) { .tc-combobox-chevron { transition: none; } }
</style>

<style>
/* Panel is teleported to <body>, so its rules are global (not scoped). */
.tc-combobox-panel {
  z-index: 1080;
  background: var(--bg-surface);
  border: 1px solid var(--stroke-2);
  border-radius: var(--r-lg);
  box-shadow: var(--shadow-28);
  overflow: hidden;
}
.tc-combobox-eyebrow {
  font-size: 11px;
  font-weight: var(--w-semibold);
  letter-spacing: .05em;
  text-transform: uppercase;
  color: var(--fg-tertiary);
  padding: var(--s-8) var(--s-10) var(--s-4);
}
.tc-combobox-list {
  list-style: none;
  margin: 0;
  padding: var(--s-4);
  max-height: 156px;
  overflow-y: auto;
  overscroll-behavior: contain;
  transition: max-height .18s ease;
}
.tc-combobox-panel.is-searching .tc-combobox-list { max-height: 280px; }
@media (prefers-reduced-motion: reduce) { .tc-combobox-list { transition: none; } }

.tc-combobox-option {
  display: flex;
  align-items: center;
  gap: var(--s-8);
  padding: var(--s-6) var(--s-8);
  border-radius: var(--r-sm);
  cursor: pointer;
}
.tc-combobox-option.is-active { background: var(--brand-tint-10); }
.tc-combobox-check { flex-shrink: 0; width: 14px; color: var(--brand-primary); font-weight: var(--w-semibold); }
.tc-combobox-option-main {
  flex: 1;
  min-width: 0;
  font-size: var(--t-13);
  color: var(--fg-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tc-combobox-option-code { flex-shrink: 0; font-size: var(--t-12); color: var(--fg-tertiary); }

.tc-combobox-loading, .tc-combobox-empty {
  padding: var(--s-12) var(--s-10);
  text-align: center;
  color: var(--fg-tertiary);
  font-size: var(--t-13);
}
</style>
